<template>
  <div
    class="border-b border-gray-200 last:border-b-0"
  >
    <div class="px-6 py-4 bg-gray-50 border-b border-gray-200">
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-3 ml-4">
          <h4
            class="text-lg font-semibold text-gray-900"
            :class="{ 'text-red-600': !trade.baselineDuration }"
          >
            {{ trade.name }}
          </h4>

          <div
            v-if="!trade.baselineDuration"
            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-3 h-3 mr-1">
    <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
</svg>

            {{ $t('HeadContract.CashflowBaseline.TradeBaselineRow.missingBaselineDates') }}
          </div>
        </div>

        <div
          v-if="trade.baselineDuration"
          class="flex items-center space-x-4 text-sm"
        >
          <div class="flex items-center space-x-2">
            <span class="text-gray-500">{{ $t('HeadContract.CashflowBaseline.TradeBaselineRow.duration') }}:</span>

            <span class="font-medium text-gray-900">{{ trade.baselineDuration }} {{ $t('HeadContract.CashflowBaseline.TradeBaselineRow.months', trade.baselineDuration) }}</span>
          </div>

          <div class="flex items-center space-x-2">
            <span class="text-gray-500">{{ $t('HeadContract.CashflowBaseline.TradeBaselineRow.allocated') }}:</span>

            <span
              :class="[totalDifference !== 0 ? 'text-red-600' : 'text-green-600']"
              class="font-medium"
            >
              {{ $t('HeadContract.CashflowBaseline.TradeBaselineRow.allocatedTotal', { allocated: $tcc(totalAmount), total: $tcc(trade.totalBudget) }) }}
              <span v-if="totalDifference !== 0">({{ (totalDifference > 0 ? "+" : "") + $tcc(totalDifference) }})</span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="flex p-2 ml-8 space-x-2">
      <div class="flex flex-col justify-center bg-gray-50 rounded-lg p-4 border-gray-200 border">
        <h5 class="text-sm font-medium text-gray-900 mb-4">
          {{ $t('HeadContract.CashflowBaseline.TradeBaselineRow.baselineDates') }}
        </h5>

        <form
          class="flex gap-4"
          :class="[trade.baselineDuration ? 'flex-col' : 'flex-row items-end']"
          @submit.prevent="updateTrade"
        >
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('HeadContract.CashflowBaseline.TradeBaselineRow.label.startDate') }}
            </label>

            <AppInput
              v-model="baselineDatesForm.form.baseline_start_date"
              name="baseline_start_date"
              type="date"
              :error="baselineDatesForm.getErrors('baseline_start_date')"
              class="form-input w-full"
              :disabled="!projectsStore.project?.unarchived"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('HeadContract.CashflowBaseline.TradeBaselineRow.label.endDate') }}
            </label>

            <AppInput
              v-model="baselineDatesForm.form.baseline_end_date"
              name="baseline_end_date"
              type="date"
              :error="baselineDatesForm.getErrors('baseline_end_date')"
              class="form-input w-full"
              :disabled="!projectsStore.project?.unarchived"
            />
          </div>

          <button
            v-if="projectsStore.project?.unarchived"
            class="btn btn--secondary-blue w-full flex items-center justify-center"
            type="submit"
            :disabled="updateTradeAction.is(States.LOADING)"
          >
            <AppSpinner
              v-if="updateTradeAction.is(States.LOADING)"
              class="mr-1"
              spinner-class="w-4 h-4"
            />

            {{ $t('HeadContract.CashflowBaseline.TradeBaselineRow.button.saveDates') }}
          </button>
        </form>
      </div>

      <div
        v-if="trade.baselineDuration"
        class="overflow-x-auto w-full bg-gray-50 rounded-lg p-4 border border-gray-200"
      >
        <div class="sticky left-0 flex flex-col">
          <div class="flex items-center justify-between mb-4">
            <h5 class="text-sm font-medium text-gray-900 mb-auto">
              {{ $t('HeadContract.CashflowBaseline.TradeBaselineRow.monthlyAllocation') }}
            </h5>

            <div class="flex items-center space-x-2">
              <template v-if="projectsStore.project?.unarchived">
                <button
                  type="button"
                  class="btn btn--tertiary-blue text-sm"
                  @click="resetBaselineLinear"
                >
                  {{ $t('HeadContract.CashflowBaseline.TradeBaselineRow.button.resetLinear') }}
                </button>

                <button
                  v-if="trade.baseline_forecast?.length > 0"
                  type="button"
                  class="btn btn--tertiary-blue text-sm"
                  @click="revertBaseline"
                >
                  {{ $t('HeadContract.CashflowBaseline.TradeBaselineRow.button.revert') }}
                </button>

                <button
                  type="button"
                  class="btn btn--secondary-blue text-sm disabled:opacity-50 flex items-center"
                  :disabled="updateBaselineForecastAction.is(States.LOADING)"
                  @click="updateBaselineForecast"
                >
                  <AppSpinner
                    v-if="updateBaselineForecastAction.is(States.LOADING)"
                    class="mr-1"
                    spinner-class="w-4 h-4"
                  />
                  {{ $t('HeadContract.CashflowBaseline.TradeBaselineRow.button.save') }}
                  <span
                    v-if="baselineForm.isDirty.value"
                    class="ml-1 text-xs"
                  >
                    {{ $t('HeadContract.CashflowBaseline.TradeBaselineRow.button.unsaved') }}
                  </span>
                </button>
              </template>
            </div>
          </div>

          <div
            v-if="baselineForm.errors['baseline_forecast']?.[0] ?? false"
            class="mb-4 p-3 bg-red-50 border border-red-200 rounded-md"
          >
            <div class="flex">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-red-400 mr-2">
    <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
</svg>

              <span class="text-sm text-red-800 first-letter:capitalize">
                {{ baselineForm.errors['baseline_forecast'][0] ?? '' }}
              </span>
            </div>
          </div>

          <div
            v-if="!forecastsSet"
            class="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md"
          >
            <div class="flex">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-yellow-400 mr-2">
    <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
</svg>

              <span class="text-sm text-yellow-800">
                {{ Math.abs(baselineForm.form.baseline_forecast.length - props.trade.baselineDuration) }} {{ $t('HeadContract.CashflowBaseline.TradeBaselineRow.forecastsMissing') }}
              </span>
            </div>
          </div>
        </div>

        <div class="flex py-5 pr-5">
          <div
            v-if="trade.baselineDuration"
            class="flex pr-5"
          >
            <table>
              <thead>
                <tr :data-month-row="trade.id">
                  <th class="sticky -left-4 bg-gray-50 pr-2" />
                </tr>
              </thead>

              <tbody>
                <tr
                  :data-percent-row="trade.id"
                >
                  <td class="sticky -left-4 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider py-2 px-3">
                    %
                  </td>
                </tr>

                <tr
                  :data-amount-row="trade.id"
                >
                  <td class="sticky -left-4 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider py-2 px-3">
                    {{ $t('currency.symbol') }}
                  </td>
                </tr>
              </tbody>
            </table>

            <template
              v-for="month in trade.baselineDuration"
              :key="month"
            >
              <BaselineInput
                v-model:percent="baselineForm.form.baseline_percents[month - 1]"
                v-model:amount="baselineForm.form.baseline_forecast[month - 1]"
                :trade="trade"
                :month-index="month"
                :error="baselineForm.hasErrors(`baseline_forecast.${month - 1}`)"
                :disabled="!projectsStore.project?.unarchived"
                @save-percent="proRataSplitPercents(month - 1, $event.oldAmount, $event.newAmount)"
              />
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, dateForDateInput, falsey, roundDecimals } from '@/helpers';
import Trade from '@/models/Trade';
import { useProjectsStore } from '@/store/projects';
import { useTradesStore } from '@/store/trades';
import Swal from 'sweetalert2';
import { computed, nextTick, watch } from 'vue';
import BaselineInput from './BaselineInput.vue';

const props = defineProps<{
  trade: Trade;
}>();

const emits = defineEmits<{
  (e: 'baselineUpdated', data: { newBaselines: number[] });
  (e: 'baselineSaved');
}>();

const projectsStore = useProjectsStore();

const baselineDatesForm = useFormData({
  baseline_start_date: '',
  baseline_end_date: '',
});

watch(
  () => [props.trade, projectsStore.project?.contract_end_date, projectsStore.project?.contract_start_date],
  (newValue) => {
    if(newValue[0]) {
      baselineDatesForm.setData((newValue as [Trade, string, string])[0]);

      if(falsey(baselineDatesForm.form.baseline_start_date)) {
        baselineDatesForm.form.baseline_start_date = dateForDateInput(projectsStore.project?.contract_start_date);
      }

      if(falsey(baselineDatesForm.form.baseline_end_date)) {
        baselineDatesForm.form.baseline_end_date = dateForDateInput(projectsStore.project?.contract_end_date);
      }
    }
  },
  { immediate: true },
);

const tradesStore = useTradesStore();

const baselineForm = useFormData({
  baseline_forecast: [] as (number[]),
  baseline_percents: [] as (number[]),
});

const totalAmount = computed(() => {
  return baselineForm.form.baseline_forecast.reduce((total, current) => {
    return total + (current || 0);
  }, 0);
});

const totalDifference = computed(() => {
  return roundDecimals(totalAmount.value - props.trade.totalBudget);
});

const forecastsSet = computed(() => {
  return baselineForm.form.baseline_forecast.length === props.trade.baselineDuration;
});

watch(
  [() => props.trade.baseline_start_date, () => props.trade.baseline_end_date, () => props.trade.baseline_forecast],
  (newValue) => {
    if(newValue) {
      // get months between baseline start/end
      // create array based on that, rather than just copying what's saved
      baselineForm.form.baseline_forecast = [];

      if(props.trade.baseline_forecast?.length > 0) {
        for(let i = 0;i < props.trade.baselineDuration;i++) {
          baselineForm.form.baseline_forecast[i] = props.trade.baseline_forecast[i] ?? 0;
        }
      }

      baselineForm.form.baseline_percents = baselineForm.form.baseline_forecast.map((forecastAmount) => {
        return roundDecimals(forecastAmount / props.trade.totalBudget * 100);
      });

      nextTick(() => {
        baselineForm.resetDirtyStatus();
      });
    }
  },
  { immediate: true },
);

const proRataSplitPercents = (index: number, oldAmount: number, newAmount: number) => {
  // get all months after

  const monthsAfter = props.trade.baselineDuration - (index + 1);

  if(monthsAfter > 0) {
    // get the difference between old and new value
    const amountDiff = oldAmount - newAmount;
    // divide difference by months after
    const amountDiffPerMonth = roundDecimals(amountDiff / monthsAfter);

    // add to each month
    // console.log(monthsAfter, amountDiffPerMonth, oldAmount, newAmount);

    let totalAmount = 0;

    for(let i = index + 1;i <= monthsAfter + index;i++) {
      // NOTE: Math.max will return NaN if it's passed in, so we convert it to 0

      baselineForm.form.baseline_forecast[i] = Math.max(
        roundDecimals(
          baselineForm.form.baseline_forecast[i] + amountDiffPerMonth,
        ) || 0,
        0,
      );
    }

    baselineForm.form.baseline_forecast.forEach((amount) => {
      totalAmount += amount || 0;
    });

    let totalDifference = roundDecimals(props.trade.totalBudget - totalAmount);
    // const additionalPercent = roundDecimals(100 - totalPercent);
    const additionalPerMonth = roundDecimals(totalDifference / monthsAfter);

    if(additionalPerMonth > 0.01) {
      for(let i = index + 1;i <= monthsAfter + index;i++) {
        // NOTE: Math.max will return NaN if it's passed in, so we convert it to 0
        baselineForm.form.baseline_forecast[i] = Math.max(
          roundDecimals(
            baselineForm.form.baseline_forecast[i] + additionalPerMonth,
          ) || 0,
          0,
        );

        totalAmount += additionalPerMonth;
      }
    }

    totalDifference = roundDecimals(props.trade.totalBudget - totalAmount);

    if(totalDifference !== 0) {
      baselineForm.form.baseline_forecast[baselineForm.form.baseline_forecast.length - 1] = Math.max(
        (baselineForm.form.baseline_forecast[baselineForm.form.baseline_forecast.length - 1] +
          roundDecimals(totalDifference)) || 0,
        0,
      );
    }
  }

  emits('baselineUpdated', { newBaselines: baselineForm.form.baseline_forecast });
};

const updateBaselineForecastAction = useStoreApiAction(tradesStore.updateBaselineForecast);

const updateBaselineForecast = () => {
  baselineForm.resetErrors();

  updateBaselineForecastAction.request(props.trade.id, baselineForm.form).then((data) => {
    Swal.fire({
      icon: 'success',
      text: 'Baseline forecast saved.',
    });

    emits('baselineSaved');
  }).catch((error) => {
    Swal.fire({
      icon: 'error',
      text: error.data.message,
    });

    baselineForm.setErrors(error.data);
  });
};

const resetBaselineLinear = () => {
  const amountPerMonth = roundDecimals(props.trade.totalBudget / props.trade.baselineDuration);
  let totalAmount = 0;

  for(let i = 0;i < props.trade.baselineDuration;i++) {
    baselineForm.form.baseline_forecast[i] = amountPerMonth;

    totalAmount += baselineForm.form.baseline_forecast[i];
  }

  if(baselineForm.form.baseline_forecast.length !== props.trade.baselineDuration) {
    baselineForm.form.baseline_forecast = baselineForm.form.baseline_forecast.slice(
      0,
      props.trade.baselineDuration - 1,
    );
  }

  const totalDifference = roundDecimals(props.trade.totalBudget - totalAmount);

  if(totalDifference !== 0) {
    baselineForm.form.baseline_forecast[baselineForm.form.baseline_forecast.length - 1] = roundDecimals(
      baselineForm.form.baseline_forecast[baselineForm.form.baseline_forecast.length - 1] +
        totalDifference,
    );
  }

  emits('baselineUpdated', { newBaselines: baselineForm.form.baseline_forecast });
};

const revertBaseline = () => {
  baselineForm.form.baseline_forecast = [];

  if(props.trade.baseline_forecast?.length > 0) {
    for(let i = 0;i < props.trade.baselineDuration;i++) {
      baselineForm.form.baseline_forecast[i] = props.trade.baseline_forecast[i] ?? 0;
    }
  }

  baselineForm.form.baseline_percents = baselineForm.form.baseline_forecast.map((forecastAmount) => {
    return roundDecimals(forecastAmount / props.trade.totalBudget * 100);
  });

  nextTick(() => {
    baselineForm.resetDirtyStatus();
  });

  emits('baselineUpdated', { newBaselines: baselineForm.form.baseline_forecast });
};

const updateTradeAction = useStoreApiAction(tradesStore.updateTrade);

const updateTrade = () => {
  baselineDatesForm.resetErrors();

  updateTradeAction.request(props.trade.id, baselineDatesForm.form).then((data) => {
    if(!props.trade.baseline_forecast) {
      // Set initial baseline linear forecast after forecast dates established
      resetBaselineLinear();
    }
  }).catch((error) => {
    baselineDatesForm.setErrors(error.data);
  });
};
</script>

<style scoped></style>
